import { vehiclePrices } from './vehiclePrices';
import { useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BYDBanner from './images/kv-pc.jpg';
import BYDBannerMobile from './images/01-kv-banner-mob.webp';
import SeagullExterior1 from './images/07-01-pc-01.webp';
import SeagullExterior2 from './images/2023-byd-seagull.jpg';
import SeagullInterior5 from './images/10-pc.jpg';
import SeagullInterior11 from './images/10-01-pc-02.webp';
import SeagullCharging15 from './images/10-01-pc-04.jpg';
import SeagullPerformance16 from './images/08-01-pc-03.webp';
import Seagull25 from './images/07-02-pc-01.webp';
import SeagullBlack from './images/04-black.webp';
import SeagullGreen from './images/byd-green.png';
import SeagullWhite from './images/03-white.webp';
import AttoShiftByWireClose from './images/atto-1-perubahan/WhatsApp Image 2026-05-15 at 9.13.15 PM.jpeg';
import AttoShiftByWireCabin from './images/atto-1-perubahan/WhatsApp Image 2026-05-15 at 9.13.16 PM.jpeg';
import AttoFixedPad from './images/atto-1-perubahan/WhatsApp Image 2026-05-15 at 9.13.17 AM.jpeg';
import AttoStalkControl from './images/atto-1-perubahan/WhatsApp Image 2026-05-15 at 9.13.17 PM.jpeg';
import AttoRetractMirror from './images/atto-1-perubahan/WhatsApp Image 2026-05-15 at asd.jpeg';
import PageSeo, {
  breadcrumbJsonLd,
  keywordSets,
  localBusinessJsonLd,
  productJsonLd,
} from './PageSeo';

function JelajahiSeagull() {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [selectedColor, setSelectedColor] = useState('black');

  const colorOptions = useMemo(
    () => [
      {
        label: 'Cosmos Black',
        value: 'black',
        swatch: '#101010',
        image: SeagullBlack,
      },
      {
        label: 'Apricity White',
        value: 'white',
        swatch: '#f3f2ee',
        image: SeagullWhite,
      },
      {
        label: 'Sprout Green',
        value: 'green',
        swatch: '#9ec15d',
        image: SeagullGreen,
      },
    ],
    [],
  );

  const selectedOption =
    colorOptions.find((option) => option.value === selectedColor) ||
    colorOptions[0];

  const heroStats = [
    { value: '300-380 km', label: 'NEDC range' },
    { value: '55 kW', label: 'Tenaga maksimum' },
    { value: '4.9 detik', label: '0-50 km/jam' },
    { value: 'Rp199-245 jt', label: 'Harga NIK 2026' },
  ];

  const pricingCards = [
    {
      variant: vehiclePrices.atto1[0].type,
      price: vehiclePrices.atto1[0].price,
      description: 'BYD Atto 1 Standard NIK 2026.',
      points: [
        'NIK 2026.',
        'Konsultasikan detail fitur dan ketersediaan unit dengan Junius.',
      ],
    },
    {
      price: vehiclePrices.atto1[1].price,
      variant: 'Dynamic',
      description:
        'Varian NIK 2026 untuk pengguna yang mencari BYD Atto 1 sebagai city car listrik kompak untuk rute harian dan mobilitas urban.',
      points: [
        'Range hingga 300 km NEDC.',
        'Blade Battery 30.08 kWh.',
        'Retract mirror, electric seat 6-way, dan shift by wire.',
      ],
    },
    {
      price: vehiclePrices.atto1[2].price,
      variant: 'Premium',
      description:
        'Varian NIK 2026 untuk pembeli yang ingin jarak tempuh lebih panjang dan fitur kenyamanan tambahan di BYD Atto 1.',
      points: [
        'Range hingga 380 km NEDC.',
        'Blade Battery 38.88 kWh.',
        'Retract mirror, electric seat 6-way, 360 camera, dan shift by wire.',
      ],
    },
  ];

  const featureUpdateCards = [
    {
      variant: 'Dynamic NIK 2026',
      points: [
        'Retract mirror dan kursi pengemudi elektrik 6-way.',
        'Shift by wire untuk ruang konsol tengah yang lebih lega.',
        'Spion eksterior berubah menjadi warna hitam.',
        'Headliner berubah ke material non-woven fabric.',
        'Defogger spion dan kaca belakang tidak tersedia.',
        'Layar PAD fixed dan tidak lagi mendukung rotasi.',
      ],
    },
    {
      variant: 'Premium NIK 2026',
      points: [
        'Retract mirror, kursi pengemudi elektrik 6-way, dan 360 camera.',
        'Shift by wire untuk ruang konsol tengah yang lebih lega.',
        'Headliner berubah ke material non-woven fabric.',
        'Defogger spion dan kaca belakang tidak tersedia.',
        'Layar PAD fixed dan tidak lagi mendukung rotasi.',
      ],
    },
  ];

  const featureUpdateImages = [
    {
      image: AttoShiftByWireClose,
      title: 'Shift by wire di balik setir',
      description:
        'Tuas transmisi pindah ke area steering column sehingga konsol tengah terasa lebih rapi dan lega.',
    },
    {
      image: AttoShiftByWireCabin,
      title: 'Layout kabin lebih praktis',
      description:
        'Posisi shift by wire membuat pengoperasian gear lebih simpel tanpa mengorbankan ruang kabin.',
    },
    {
      image: AttoFixedPad,
      title: 'Layar PAD fixed',
      description:
        'Layar sentuh tetap menjadi pusat kontrol, namun pada update NIK 2026 tidak lagi mendukung rotasi.',
    },
    {
      image: AttoStalkControl,
      title: 'Kontrol lampu dan wiper',
      description:
        'Stalk control tetap mudah dijangkau untuk kebutuhan harian seperti sein, lampu, dan wiper.',
    },
    {
      image: AttoRetractMirror,
      title: 'Retract mirror dan window control',
      description:
        'Fitur retract mirror menjadi salah satu update penting untuk Dynamic dan Premium NIK 2026.',
    },
  ];

  const localAreaCards = [
    {
      area: 'BSD Serpong',
      title: 'Halaman paling relevan untuk pencarian BYD Atto 1 BSD',
      description:
        'Showroom utama berada di BSD Serpong, jadi intent seperti harga, promo, booking unit, dan test drive bisa diarahkan ke flow pembelian yang paling dekat ke lokasi dealer.',
      points: [
        'Cocok untuk pencarian BYD Atto 1 BSD atau BYD Atto 1 BSD Serpong.',
        'Memudahkan user lanjut dari riset ke konsultasi dan test drive.',
      ],
    },
    {
      area: 'Tangerang',
      title: 'Menangkap intent komersial dari pembeli area Tangerang',
      description:
        'Calon pembeli dari Tangerang biasanya tidak hanya mencari spesifikasi, tetapi juga harga OTR, promo aktif, simulasi cicilan, dan cara melihat unit secara langsung.',
      points: [
        'Menarget query seperti BYD Atto 1 Tangerang dan harga BYD Atto 1 Tangerang.',
        'Fokusnya tetap ke showroom BSD Serpong sebagai titik transaksi utama.',
      ],
    },
    {
      area: 'Gading Serpong',
      title:
        'Melayani pencarian dari Gading Serpong tanpa bikin city-page tipis',
      description:
        'Alih-alih membuat halaman kota yang isinya duplikat, halaman utama ini menjelaskan bahwa showroom BSD Serpong juga relevan untuk calon pembeli dari Gading Serpong dan area sekitarnya.',
      points: [
        'Menjawab intent BYD Atto 1 Gading Serpong secara natural.',
        'Tetap aman dari pola doorway page karena kontennya unik dan kontekstual.',
      ],
    },
  ];

  const ownershipHighlights = [
    {
      title: 'Blade Battery',
      text: 'Baterai LFP khas BYD dirancang untuk efisiensi, keamanan, dan pemakaian harian yang konsisten.',
    },
    {
      title: 'e-Platform 3.0',
      text: 'Platform EV BYD membantu Atto 1 terasa modern, ringkas, dan relevan untuk kebutuhan urban.',
    },
    {
      title: 'Top Speed 130 km/jam',
      text: 'Cukup untuk kombinasi mobilitas kota, jalan besar, dan kebutuhan komuter harian.',
    },
    {
      title: 'Android Auto & CarPlay',
      text: 'Koneksi smartphone membuat navigasi, musik, dan komunikasi lebih praktis saat berkendara.',
    },
  ];

  const specComparisonRows = [
    {
      label: 'Harga indikatif NIK 2026',
      dynamic: 'Rp210 juta',
      premium: 'Rp245 juta',
    },
    { label: 'Jarak tempuh NEDC', dynamic: '300 km', premium: '380 km' },
    { label: 'Kapasitas baterai', dynamic: '30.08 kWh', premium: '38.88 kWh' },
    { label: 'Tenaga maksimum', dynamic: '55 kW', premium: '55 kW' },
    { label: 'Torsi maksimum', dynamic: '135 Nm', premium: '135 Nm' },
    {
      label: 'Kecepatan maksimum',
      dynamic: '130 km/jam',
      premium: '130 km/jam',
    },
    {
      label: 'Radius putar minimum',
      dynamic: '4.95 meter',
      premium: '4.95 meter',
    },
    { label: 'Bobot kosong', dynamic: '1.225 kg', premium: '1.280 kg' },
    { label: 'Pengisian cepat DC', dynamic: '30 kW', premium: '40 kW' },
    {
      label: 'Kursi pengemudi',
      dynamic: 'Electric 6-way',
      premium: 'Electric 6-way',
    },
    {
      label: 'Spion',
      dynamic: 'Electric / retract',
      premium: 'Electric / retract',
    },
    { label: 'Kamera 360', dynamic: 'Tidak tersedia', premium: 'Tersedia' },
  ];

  const relatedResources = [
    {
      title: 'Harga BYD Atto 1 BSD, Tangerang, dan Gading Serpong',
      description:
        'Halaman lokal-komersial untuk promo, simulasi pembelian, dan konsultasi harga BYD Atto 1 dari showroom BSD Serpong.',
      path: '/blog/harga-byd-atto-1-bsd-serpong-junius',
      cta: 'Lihat harga dan promo',
    },
    {
      title: 'Perubahan fitur BYD Atto 1 NIK 2026',
      description:
        'Ringkasan update Dynamic dan Premium, termasuk shift by wire, retract mirror, electric seat di kedua varian, 360 camera Premium, layar PAD fixed, dan fitur yang disesuaikan.',
      path: '/blog/perubahan-fitur-byd-atto-1-nik-2026',
      cta: 'Lihat perubahan fitur',
    },
    {
      title: 'Konsultasi BYD Atto 1 dengan Junius',
      description:
        'Halaman support untuk kontak sales, bantuan booking test drive, dan follow up pembelian bersama Junius di BYD BSD Serpong.',
      path: '/blog/byd-atto-1-bsd-serpong-junius',
      cta: 'Buka halaman konsultasi',
    },
    {
      title: 'Pilihan warna BYD Atto 1',
      description:
        'Cek warna Atto 1 untuk bantu pembeli membandingkan tampilan eksterior sebelum booking unit.',
      path: '/blog/pilihan-warna-byd-atto1',
      cta: 'Lihat pilihan warna',
    },
    {
      title: 'Interior BYD Atto 1',
      description:
        'Pembahasan kabin, dashboard, layout, dan fitur yang paling terasa untuk penggunaan harian.',
      path: '/blog/interior-byd-atto1',
      cta: 'Lihat interior',
    },
    {
      title: 'Eksterior BYD Atto 1',
      description:
        'Bahas ukuran bodi, desain muka, lampu, dan kesan visual Atto 1 di jalanan kota.',
      path: '/blog/eksterior-byd-atto1',
      cta: 'Lihat eksterior',
    },
    {
      title: 'Fitur BYD Atto 1',
      description:
        'Ringkasan fitur keselamatan, charging, baterai, dan teknologi yang jadi pertimbangan pembeli.',
      path: '/blog/fitur-byd-atto1',
      cta: 'Lihat fitur lengkap',
    },
  ];

  const faqItems = [
    {
      question: 'Berapa harga BYD Atto 1 di BSD Serpong?',
      answer:
        'Harga indikatif BYD Atto 1 NIK 2026 di halaman ini Rp199 juta untuk Standard, Rp210 juta untuk Dynamic, dan Rp245 juta untuk Premium. Harga, promo, dan unit dapat berubah, jadi konfirmasi terbaru sebaiknya langsung ke Junius di BYD BSD Serpong.',
    },
    {
      question: 'Apa update fitur BYD Atto 1 NIK 2026?',
      answer:
        'Dynamic NIK 2026 mendapat retract mirror, kursi pengemudi elektrik 6-way, shift by wire, spion hitam, headliner non-woven fabric, defogger spion dan kaca belakang tidak tersedia, serta layar PAD fixed. Premium NIK 2026 mendapat retract mirror, kursi pengemudi elektrik 6-way, 360 camera, shift by wire, airbag menjadi 4, headliner non-woven fabric, defogger tidak tersedia, dan layar PAD fixed.',
    },
    {
      question: 'Apakah BYD Atto 1 relevan untuk pencarian area Tangerang?',
      answer:
        'Ya. Showroom BSD Serpong melayani pembeli dari Tangerang dan Tangerang Selatan, jadi keyword seperti BYD Atto 1 Tangerang paling tepat diarahkan ke halaman produk utama dan halaman harga lokal.',
    },
    {
      question:
        'Kalau saya cari BYD Atto 1 Gading Serpong, apakah tetap bisa dilayani?',
      answer:
        'Bisa. Halaman ini tetap relevan untuk pembeli dari Gading Serpong karena showroom BSD Serpong berada di area yang masih dekat dan cocok untuk konsultasi unit, harga, warna, hingga booking test drive.',
    },
    {
      question: 'Apakah bisa test drive BYD Atto 1 di BSD Serpong?',
      answer:
        'Bisa. Anda dapat menghubungi Junius untuk cek jadwal test drive BYD Atto 1 sesuai ketersediaan unit dan slot test drive.',
    },
    {
      question: 'Apa keunggulan BYD Atto 1 untuk pemakaian harian?',
      answer:
        'Ukuran bodi yang kompak, efisiensi EV, fitur konektivitas modern, dan karakter city car membuat BYD Atto 1 cocok untuk mobilitas urban dan komuter harian.',
    },
    {
      question: 'Berapa radius putar BYD Atto 1?',
      answer:
        'Radius putar minimum BYD Atto 1 adalah sekitar 4,95 meter. Ukuran ini membantu mobil bermanuver di jalan kota, area parkir, dan ruang yang relatif sempit.',
    },
    {
      question: 'Berapa bobot BYD Atto 1 Dynamic dan Premium?',
      answer:
        'Bobot kosong BYD Atto 1 Dynamic sekitar 1.225 kg, sedangkan Premium sekitar 1.280 kg.',
    },
    {
      question: 'Apa pilihan warna BYD Atto 1 NIK 2026?',
      answer:
        'Pilihan warna yang ditampilkan untuk BYD Atto 1 NIK 2026 adalah Cosmos Black, Apricity White, dan Sprout Green. Ketersediaan warna perlu dikonfirmasi karena stok unit dapat berubah.',
    },
    {
      question: 'Halaman mana yang paling tepat untuk keyword BYD Atto 1?',
      answer:
        'Halaman ini difokuskan sebagai landing page utama untuk pencarian umum seputar BYD Atto 1, sementara halaman lain membahas intent yang lebih spesifik seperti harga, warna, interior, dan fitur.',
    },
  ];

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <PageSeo
        title='BYD Atto 1 NIK 2026 | Harga, Fitur, Promo BSD Tangerang'
        description='BYD Atto 1 NIK 2026 di BYD BSD Serpong: Standard Rp199 juta, Dynamic Rp210 juta, Premium Rp245 juta, update fitur, promo, warna, dan booking test drive bersama Junius.'
        keywords={[
          ...keywordSets.localDealer,
          'BYD Atto 1',
          'harga BYD Atto 1',
          'spesifikasi BYD Atto 1',
          'promo BYD Atto 1',
          'test drive BYD Atto 1',
          'BYD Atto 1 Indonesia',
          'BYD Atto 1 BSD Serpong',
          'BYD Atto 1 BSD',
          'BYD Atto 1 Tangerang',
          'BYD Atto 1 Gading Serpong',
          'harga BYD Atto 1 BSD',
          'city car listrik BYD',
        ]}
        path='/jelajahi-atto1'
        image={BYDBanner}
        jsonLd={[
          localBusinessJsonLd(),
          productJsonLd({
            name: 'BYD Atto 1',
            description:
              'City car listrik BYD Atto 1 NIK 2026 dengan Blade Battery, layar 10.1 inci fixed PAD, CarPlay, Android Auto, serta varian Standard, Dynamic, dan Premium.',
            image: BYDBanner,
            path: '/jelajahi-atto1',
            lowPrice: vehiclePrices.atto1[0].priceValue,
            highPrice: vehiclePrices.atto1[2].priceValue,
            offerCount: 3,
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Atto 1', path: '/jelajahi-atto1' },
          ]),
          faqJsonLd,
        ]}
      />

      <Box component='main' sx={{ width: '100%', overflow: 'hidden' }}>
        <HeroSection isMobile={isMobile} heroStats={heroStats} />
        <OverviewSection />
        <LocalAreaSection localAreaCards={localAreaCards} />
        <PricingSection pricingCards={pricingCards} />
        <FeatureUpdateSection featureUpdateCards={featureUpdateCards} />
        <FeatureUpdateGallerySection
          featureUpdateImages={featureUpdateImages}
        />
        <ColorSection
          colorOptions={colorOptions}
          selectedOption={selectedOption}
          selectedColor={selectedColor}
          setSelectedColor={setSelectedColor}
        />
        <FeatureBand
          image={SeagullExterior1}
          title='BYD Atto 1 mudah dipakai di jalan kota'
          description='Dimensi 3925 x 1720 x 1550 mm membantu BYD Atto 1 masuk area padat, parkir di ruang terbatas, dan tetap nyaman untuk kebutuhan harian di kota.'
        />
        <FeatureBand
          image={SeagullInterior5}
          title='Kabin BYD Atto 1 terasa modern dan praktis'
          description='Layar sentuh 10.1 inci, panel instrumen digital, konektivitas smartphone, dan layout kabin yang simpel membuat perjalanan lebih praktis untuk pemakaian sehari-hari.'
          reverse
        />
        <SpecificationTableSection rows={specComparisonRows} />
        <OwnershipSection ownershipHighlights={ownershipHighlights} />
        <FeatureGrid />
        <FeatureBand
          image={SeagullExterior2}
          title='Desain BYD Atto 1 tetap muda dan futuristik'
          description='Garis bodi tegas, velg 16 inci, dan DRL LED memberi karakter city car listrik yang segar tanpa terasa berlebihan, cocok untuk pembeli yang mencari mobil listrik kompak dengan tampilan modern.'
        />
        <ResourceSection relatedResources={relatedResources} />
        <FaqSection faqItems={faqItems} />
        <CtaSection />
      </Box>
    </>
  );
}

function HeroSection({ isMobile, heroStats }) {
  return (
    <Box
      component='section'
      aria-labelledby='hero-atto1-title'
      sx={{
        position: 'relative',
        minHeight: { xs: '92vh', md: '100vh' },
        pt: { xs: 9, md: 10 },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        backgroundColor: '#101418',
      }}
    >
      <Box
        component='img'
        src={isMobile ? BYDBannerMobile : BYDBanner}
        alt='BYD Atto 1 city car listrik'
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.08) 44%, rgba(0,0,0,0.68) 100%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pb: { xs: 5, md: 7 },
          color: '#fff',
        }}
      >
        <Typography
          sx={{
            display: 'inline-flex',
            px: 1.2,
            py: 0.65,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
            fontSize: '0.78rem',
            fontWeight: 900,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          BYD BSD Serpong
        </Typography>
        <Typography
          id='hero-atto1-title'
          component='h1'
          sx={{
            mt: 1.6,
            fontSize: { xs: '2.35rem', sm: '3.5rem', md: '5rem' },
            fontWeight: 900,
            lineHeight: 0.98,
            maxWidth: 760,
          }}
        >
          BYD Atto 1
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 720,
            fontSize: { xs: '1rem', md: '1.12rem' },
            lineHeight: 1.75,
            color: 'rgba(255,255,255,0.92)',
          }}
        >
          BYD Atto 1 adalah city car listrik kompak untuk mobilitas harian,
          dengan Blade Battery, ukuran ringkas, dan fitur modern untuk pembeli
          yang mencari EV praktis di BSD, Tangerang, Gading Serpong, dan area
          sekitarnya.
        </Typography>

        <Box
          sx={{
            mt: { xs: 3, md: 4 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 1,
            maxWidth: 850,
          }}
        >
          {heroStats.map((spec) => (
            <Box
              key={spec.label}
              sx={{
                border: '1px solid rgba(255,255,255,0.24)',
                backgroundColor: 'rgba(0,0,0,0.36)',
                px: 1.8,
                py: 1.4,
                borderRadius: '8px',
              }}
            >
              <Typography sx={{ fontSize: '1.18rem', fontWeight: 900 }}>
                {spec.value}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', opacity: 0.82 }}>
                {spec.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 3,
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            component={RouterLink}
            to='/blog/harga-byd-atto-1-bsd-serpong-junius'
            variant='contained'
            sx={{
              px: 3.2,
              py: 1.1,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#1f5fd1',
              '&:hover': { backgroundColor: '#184ca8' },
            }}
          >
            Lihat Harga BYD Atto 1
          </Button>
          <Button
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20BYD%20Atto%201%20dan%20ingin%20tahu%20harga%20serta%20promonya'
            target='_blank'
            rel='noopener noreferrer'
            variant='outlined'
            sx={{
              px: 3.2,
              py: 1.1,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 900,
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.72)',
              '&:hover': {
                borderColor: '#fff',
                backgroundColor: 'rgba(255,255,255,0.12)',
              },
            }}
          >
            Tanya Promo dan Test Drive
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function OverviewSection() {
  return (
    <Box
      component='section'
      aria-labelledby='overview-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#4c5867',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            BYD Atto 1 Indonesia
          </Typography>
          <Typography
            id='overview-atto1-title'
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3.2rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.05,
            }}
          >
            Halaman utama untuk cari harga, spesifikasi, dan warna BYD Atto 1
          </Typography>
        </Box>
        <Typography
          sx={{
            color: '#4b5563',
            fontSize: { xs: '1rem', md: '1.08rem' },
            lineHeight: 1.8,
          }}
        >
          Jika orang mencari <strong>BYD Atto 1</strong>, biasanya yang ingin
          diketahui bukan cuma foto mobilnya, tetapi juga kisaran harga,
          spesifikasi penting, pilihan warna, fitur, dan cara test drive. Karena
          itu halaman ini difokuskan sebagai landing page utama untuk intent
          umum, sementara halaman pendukung lain membahas harga, warna,
          interior, eksterior, dan fitur secara lebih spesifik. Untuk query
          lokal seperti <strong>BYD Atto 1 BSD</strong>,{' '}
          <strong>BYD Atto 1 Tangerang</strong>, dan{' '}
          <strong>BYD Atto 1 Gading Serpong</strong>, halaman ini tetap jadi
          pusat informasi utama sebelum user lanjut ke halaman harga.
        </Typography>
      </Box>
    </Box>
  );
}

function LocalAreaSection({ localAreaCards }) {
  return (
    <Box
      component='section'
      aria-labelledby='local-area-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#111827',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto', color: '#fff' }}>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.72)',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Area Layanan
        </Typography>
        <Typography
          id='local-area-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            lineHeight: 1.05,
            maxWidth: 860,
          }}
        >
          Satu halaman utama yang tetap relevan untuk BSD, Tangerang, dan Gading
          Serpong
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 860,
            color: 'rgba(255,255,255,0.78)',
            lineHeight: 1.8,
          }}
        >
          Pendekatan ini sengaja dipakai agar keyword area tetap tertangkap
          tanpa membuat banyak halaman kota yang isinya serupa. URL utama fokus
          ke pencarian produk, lalu halaman harga menangkap intent lokal yang
          lebih dekat ke transaksi.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {localAreaCards.map((card) => (
            <Box
              key={card.area}
              sx={{
                p: 2.4,
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.12)',
                backgroundColor: 'rgba(255,255,255,0.06)',
              }}
            >
              <Typography
                sx={{
                  color: '#a7c5ff',
                  fontSize: '0.82rem',
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {card.area}
              </Typography>
              <Typography
                component='h3'
                sx={{
                  mt: 0.8,
                  fontSize: '1.35rem',
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {card.title}
              </Typography>
              <Typography
                sx={{
                  mt: 1.2,
                  color: 'rgba(255,255,255,0.76)',
                  lineHeight: 1.7,
                }}
              >
                {card.description}
              </Typography>
              <Box
                component='ul'
                sx={{
                  mt: 2,
                  mb: 0,
                  pl: 2.3,
                  color: 'rgba(255,255,255,0.82)',
                }}
              >
                {card.points.map((point) => (
                  <Box
                    component='li'
                    key={point}
                    sx={{ mb: 0.8, lineHeight: 1.6 }}
                  >
                    {point}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function PricingSection({ pricingCards }) {
  return (
    <Box
      component='section'
      aria-labelledby='pricing-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Harga BYD Atto 1
        </Typography>
        <Typography
          id='pricing-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 760,
          }}
        >
          Harga NIK 2026 dan varian BYD Atto 1 untuk pembeli BSD dan Tangerang
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 820,
            color: '#4b5563',
            lineHeight: 1.75,
          }}
        >
          Untuk pencarian seperti <strong>harga BYD Atto 1</strong>, halaman ini
          memberi gambaran awal harga NIK 2026 sebelum Anda masuk ke halaman
          promo atau konsultasi unit. Harga dapat berubah sesuai promo, OTR,
          warna, dan ketersediaan unit. Jika intent Anda sudah lebih lokal
          seperti <strong>BYD Atto 1 BSD</strong> atau{' '}
          <strong>BYD Atto 1 Tangerang</strong>, lanjutkan ke halaman harga
          untuk info yang lebih dekat ke transaksi.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {pricingCards.map((card) => (
            <Box
              key={card.variant}
              sx={{
                p: { xs: 2.2, md: 2.8 },
                borderRadius: '8px',
                border: '1px solid rgba(17,24,39,0.08)',
                backgroundColor: '#f8fafc',
                boxShadow: '0 12px 28px rgba(16,24,40,0.06)',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: '#2d6cdf',
                }}
              >
                Varian
              </Typography>
              <Typography
                component='h3'
                sx={{
                  mt: 0.6,
                  fontSize: { xs: '1.6rem', md: '1.9rem' },
                  fontWeight: 900,
                  color: '#111827',
                }}
              >
                {card.variant}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: '1.05rem',
                  fontWeight: 900,
                  color: '#11805a',
                }}
              >
                {card.price}
              </Typography>
              <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.7 }}>
                {card.description}
              </Typography>
              <Box
                component='ul'
                sx={{ mt: 2, mb: 0, pl: 2.4, color: '#111827' }}
              >
                {card.points.map((point) => (
                  <Box
                    component='li'
                    key={point}
                    sx={{ mb: 0.8, lineHeight: 1.6, color: '#4b5563' }}
                  >
                    {point}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ mt: 2, color: '#6b7280', fontSize: '0.92rem' }}>
          *Harga indikatif NIK 2026 OTR BSD Serpong dan dapat berubah
          sewaktu-waktu.
        </Typography>
        <Button
          component={RouterLink}
          to='/blog/harga-byd-atto-1-bsd-serpong-junius'
          variant='text'
          sx={{
            mt: 1,
            px: 0,
            textTransform: 'none',
            fontWeight: 900,
            color: '#1f5fd1',
          }}
        >
          Buka halaman harga lokal BYD Atto 1
        </Button>
      </Box>
    </Box>
  );
}

function FeatureUpdateSection({ featureUpdateCards }) {
  return (
    <Box
      component='section'
      aria-labelledby='feature-update-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Update Fitur NIK 2026
        </Typography>
        <Typography
          id='feature-update-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 820,
          }}
        >
          Perubahan fitur BYD Atto 1 Dynamic dan Premium NIK 2026
        </Typography>
        <Typography
          sx={{ mt: 1.5, maxWidth: 820, color: '#4b5563', lineHeight: 1.75 }}
        >
          Beberapa fitur bertambah, sementara beberapa fitur lain disesuaikan.
          Gunakan ringkasan ini untuk membandingkan Dynamic dan Premium sebelum
          cek stok atau booking test drive.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          {featureUpdateCards.map((card) => (
            <Box
              key={card.variant}
              sx={{
                p: { xs: 2.2, md: 2.8 },
                borderRadius: '8px',
                backgroundColor: '#fff',
                border: '1px solid rgba(17,24,39,0.08)',
                boxShadow: '0 12px 28px rgba(16,24,40,0.06)',
              }}
            >
              <Typography
                component='h3'
                sx={{
                  fontSize: { xs: '1.45rem', md: '1.75rem' },
                  fontWeight: 900,
                  color: '#111827',
                }}
              >
                {card.variant}
              </Typography>
              <Box
                component='ul'
                sx={{ mt: 2, mb: 0, pl: 2.4, color: '#111827' }}
              >
                {card.points.map((point) => (
                  <Box
                    component='li'
                    key={point}
                    sx={{ mb: 0.8, lineHeight: 1.6, color: '#4b5563' }}
                  >
                    {point}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FeatureUpdateGallerySection({ featureUpdateImages }) {
  return (
    <Box
      component='section'
      aria-labelledby='feature-update-gallery-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Foto Update NIK 2026
        </Typography>
        <Typography
          id='feature-update-gallery-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 860,
          }}
        >
          Detail perubahan Atto 1 baru yang perlu dilihat sebelum booking
        </Typography>
        <Typography
          sx={{ mt: 1.5, maxWidth: 840, color: '#4b5563', lineHeight: 1.75 }}
        >
          Foto ini membantu menjelaskan update fitur BYD Atto 1 NIK 2026 secara
          lebih konkret, terutama shift by wire, layar PAD fixed, dan retract
          mirror yang sering ditanyakan calon pembeli.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: 'repeat(5, 1fr)',
            },
            gap: 2,
          }}
        >
          {featureUpdateImages.map((item) => (
            <Box
              key={item.title}
              sx={{
                overflow: 'hidden',
                borderRadius: '8px',
                backgroundColor: '#f8fafc',
                border: '1px solid rgba(17,24,39,0.08)',
                boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
              }}
            >
              <Box
                component='img'
                src={item.image}
                alt={item.title}
                loading='lazy'
                sx={{
                  width: '100%',
                  height: { xs: 260, sm: 250, lg: 220 },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <Box sx={{ p: 2 }}>
                <Typography
                  sx={{ fontWeight: 900, color: '#111827', lineHeight: 1.25 }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.8,
                    color: '#4b5563',
                    lineHeight: 1.6,
                    fontSize: '0.92rem',
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ColorSection({
  colorOptions,
  selectedOption,
  selectedColor,
  setSelectedColor,
}) {
  return (
    <Box
      component='section'
      aria-labelledby='color-atto1-title'
      sx={{
        minHeight: { xs: 'auto', md: '100vh' },
        backgroundColor: '#fff',
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 360px' },
          gap: { xs: 4, lg: 5 },
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              color: '#4c5867',
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontSize: '0.82rem',
            }}
          >
            Warna BYD Atto 1
          </Typography>
          <Typography
            id='color-atto1-title'
            component='h2'
            sx={{
              mt: 1,
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              color: '#111827',
              lineHeight: 1.05,
            }}
          >
            Pilih warna BYD Atto 1
          </Typography>
          <Typography
            sx={{ mt: 1.5, maxWidth: 720, color: '#4b5563', lineHeight: 1.7 }}
          >
            Warna adalah salah satu intent pencarian yang kuat untuk calon
            pembeli BYD Atto 1. Bagian ini membantu user melihat warna populer
            sebelum lanjut ke artikel pendukung atau konsultasi unit.
          </Typography>
          <Box
            sx={{
              mt: 2,
              height: { xs: 270, sm: 380, md: 500 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='img'
              src={selectedOption.image}
              alt={`BYD Atto 1 warna ${selectedOption.label}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'drop-shadow(0 24px 24px rgba(16,24,40,0.2))',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '8px',
            border: '1px solid rgba(17,24,39,0.12)',
            backgroundColor: '#f8fafc',
            p: { xs: 2, sm: 3 },
            boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
          }}
        >
          <Typography
            sx={{
              color: '#667085',
              fontWeight: 800,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              fontSize: '0.78rem',
            }}
          >
            Selected color
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '2rem', fontWeight: 900 }}>
            {selectedOption.label}
          </Typography>
          <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.6 }}>
            Pilih warna eksterior yang paling cocok dengan gaya harian kamu.
          </Typography>

          <Box sx={{ mt: 3, display: 'grid', gap: 1 }}>
            {colorOptions.map((option) => {
              const active = selectedColor === option.value;

              return (
                <Button
                  key={option.value}
                  type='button'
                  onClick={() => setSelectedColor(option.value)}
                  variant='outlined'
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    py: 1.2,
                    px: 1.3,
                    borderRadius: '8px',
                    textTransform: 'none',
                    color: '#111827',
                    borderColor: active ? '#2d6cdf' : 'rgba(17,24,39,0.16)',
                    backgroundColor: active ? '#edf4ff' : '#fff',
                    '&:hover': {
                      borderColor: '#2d6cdf',
                      backgroundColor: active ? '#e4efff' : '#f7f9fc',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      flex: '0 0 auto',
                      backgroundColor: option.swatch,
                      border:
                        option.value === 'white'
                          ? '1px solid rgba(17,24,39,0.24)'
                          : '1px solid rgba(255,255,255,0.42)',
                      boxShadow: active
                        ? '0 0 0 3px rgba(45,108,223,0.22)'
                        : '',
                    }}
                  />
                  <Typography sx={{ fontWeight: 800 }}>
                    {option.label}
                  </Typography>
                </Button>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FeatureBand({ image, title, description, reverse = false }) {
  return (
    <Box
      component='section'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: reverse ? '#111827' : '#f4f7fb',
      }}
    >
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'center',
        }}
      >
        <Box sx={{ order: { xs: 1, md: reverse ? 2 : 1 } }}>
          <Box
            component='img'
            src={image}
            alt={title}
            sx={{
              width: '100%',
              height: { xs: 280, sm: 390, md: 500 },
              objectFit: 'cover',
              borderRadius: '8px',
              display: 'block',
              boxShadow: reverse
                ? '0 24px 42px rgba(0,0,0,0.34)'
                : '0 18px 32px rgba(16,24,40,0.12)',
            }}
          />
        </Box>
        <Box
          sx={{
            order: { xs: 2, md: reverse ? 1 : 2 },
            color: reverse ? '#fff' : '#111827',
          }}
        >
          <Typography
            component='h2'
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              lineHeight: 1.08,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 2,
              color: reverse ? 'rgba(255,255,255,0.78)' : '#4b5563',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', md: '1.08rem' },
            }}
          >
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function SpecificationTableSection({ rows }) {
  return (
    <Box
      component='section'
      aria-labelledby='spec-table-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Spesifikasi NIK 2026
        </Typography>
        <Typography
          id='spec-table-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 860,
          }}
        >
          Perbandingan spesifikasi BYD Atto 1 Dynamic dan Premium
        </Typography>
        <Typography
          sx={{ mt: 1.5, maxWidth: 840, color: '#4b5563', lineHeight: 1.75 }}
        >
          Tabel ini merangkum informasi yang paling sering dicari, termasuk
          harga, baterai, jarak tempuh, radius putar, bobot, kursi elektrik,
          spion elektrik, dan kamera 360.
        </Typography>

        <Box sx={{ mt: 3, overflowX: 'auto', borderRadius: '8px' }}>
          <Box
            sx={{
              minWidth: 700,
              border: '1px solid rgba(17,24,39,0.1)',
              backgroundColor: '#fff',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1fr 1fr',
                backgroundColor: '#111827',
                color: '#fff',
              }}
            >
              {['Spesifikasi', 'Dynamic', 'Premium'].map((heading) => (
                <Typography key={heading} sx={{ p: 1.8, fontWeight: 900 }}>
                  {heading}
                </Typography>
              ))}
            </Box>
            {rows.map((row, index) => (
              <Box
                key={row.label}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 1fr',
                  backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc',
                  borderTop: '1px solid rgba(17,24,39,0.08)',
                }}
              >
                <Typography sx={{ p: 1.7, fontWeight: 800, color: '#111827' }}>
                  {row.label}
                </Typography>
                <Typography sx={{ p: 1.7, color: '#4b5563' }}>
                  {row.dynamic}
                </Typography>
                <Typography sx={{ p: 1.7, color: '#4b5563' }}>
                  {row.premium}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Typography sx={{ mt: 1.5, color: '#6b7280', fontSize: '0.9rem' }}>
          Harga, spesifikasi, dan ketersediaan unit dapat berubah. Konfirmasi
          data terbaru sebelum pemesanan.
        </Typography>
      </Box>
    </Box>
  );
}

function OwnershipSection({ ownershipHighlights }) {
  return (
    <Box
      component='section'
      aria-labelledby='ownership-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Spesifikasi BYD Atto 1
        </Typography>
        <Typography
          id='ownership-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 780,
          }}
        >
          Hal-hal yang paling sering dicari sebelum beli BYD Atto 1
        </Typography>
        <Typography
          sx={{ mt: 1.5, maxWidth: 800, color: '#4b5563', lineHeight: 1.75 }}
        >
          Pembeli yang mencari <strong>spesifikasi BYD Atto 1</strong> biasanya
          fokus pada baterai, platform EV, konektivitas, dan kemampuan mobil
          untuk dipakai harian. Empat poin ini adalah inti yang paling relevan
          di pencarian awal.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {ownershipHighlights.map((item) => (
            <Box
              key={item.title}
              sx={{
                p: 2.3,
                borderRadius: '8px',
                border: '1px solid rgba(17,24,39,0.08)',
                backgroundColor: '#f8fafc',
              }}
            >
              <Typography
                sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#111827' }}
              >
                {item.title}
              </Typography>
              <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.7 }}>
                {item.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FeatureGrid() {
  const items = [
    {
      image: SeagullInterior11,
      title: 'Kontrol praktis',
      desc: 'Setir multifungsi, head unit intuitif, shift by wire, dan pengaturan kursi elektrik sesuai varian.',
    },
    {
      image: SeagullCharging15,
      title: 'Bagasi fleksibel',
      desc: 'Kursi belakang dapat dilipat untuk membawa barang lebih banyak saat dibutuhkan.',
    },
    {
      image: SeagullPerformance16,
      title: 'Keselamatan lengkap',
      desc: 'ABS, EBD, ESC, TPMS, airbag, ISOFIX, dan pengereman regeneratif untuk pemakaian harian.',
    },
    {
      image: Seagull25,
      title: 'Lincah di kota',
      desc: 'Radius putar 4.95 meter membuat manuver di jalan sempit dan area parkir lebih mudah.',
    },
  ];

  return (
    <Box
      component='section'
      sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.08,
            mb: 1,
          }}
        >
          Fitur BYD Atto 1 yang terasa setiap hari
        </Typography>
        <Typography
          sx={{ mb: 3, color: '#4b5563', lineHeight: 1.75, maxWidth: 760 }}
        >
          Section ini membantu memperkuat intent pencarian fitur tanpa membuat
          halaman utama kehilangan fokus sebagai landing page produk.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2.5,
          }}
        >
          {items.map((item) => (
            <Box
              key={item.title}
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 12px 26px rgba(16,24,40,0.08)',
                border: '1px solid rgba(17,24,39,0.08)',
              }}
            >
              <Box
                component='img'
                src={item.image}
                alt={item.title}
                sx={{
                  width: '100%',
                  height: { xs: 210, sm: 240 },
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '1.25rem' }}>
                  {item.title}
                </Typography>
                <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.65 }}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ResourceSection({ relatedResources }) {
  return (
    <Box
      component='section'
      aria-labelledby='resource-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#f4f7fb',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography
          sx={{
            color: '#4c5867',
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: '0.82rem',
          }}
        >
          Internal Link Cluster
        </Typography>
        <Typography
          id='resource-atto1-title'
          component='h2'
          sx={{
            mt: 1,
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
            maxWidth: 820,
          }}
        >
          Artikel pendukung BYD Atto 1 untuk harga, warna, fitur, dan test drive
        </Typography>
        <Typography
          sx={{ mt: 1.5, maxWidth: 780, color: '#4b5563', lineHeight: 1.75 }}
        >
          Dengan memisahkan intent pencarian ke halaman pendukung, URL utama
          <strong> /jelajahi-atto1</strong> tetap fokus ke keyword head term
          <strong> BYD Atto 1</strong>, sementara artikel cluster menangkap
          long-tail yang lebih spesifik.
        </Typography>

        <Box
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              xl: 'repeat(3, 1fr)',
            },
            gap: 2,
          }}
        >
          {relatedResources.map((resource) => (
            <Box
              key={resource.path}
              sx={{
                p: 2.3,
                borderRadius: '8px',
                backgroundColor: '#fff',
                border: '1px solid rgba(17,24,39,0.08)',
                boxShadow: '0 12px 26px rgba(16,24,40,0.06)',
              }}
            >
              <Typography
                sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#111827' }}
              >
                {resource.title}
              </Typography>
              <Typography sx={{ mt: 1, color: '#4b5563', lineHeight: 1.7 }}>
                {resource.description}
              </Typography>
              <Button
                component={RouterLink}
                to={resource.path}
                variant='text'
                sx={{
                  mt: 1.3,
                  px: 0,
                  textTransform: 'none',
                  fontWeight: 900,
                  color: '#1f5fd1',
                }}
              >
                {resource.cta}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function FaqSection({ faqItems }) {
  return (
    <Box
      component='section'
      aria-labelledby='faq-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Typography
          id='faq-atto1-title'
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
            color: '#111827',
            lineHeight: 1.05,
          }}
        >
          FAQ BYD Atto 1
        </Typography>
        <Box sx={{ mt: 2.5, display: 'grid', gap: 1.4 }}>
          {faqItems.map((item) => (
            <Box
              key={item.question}
              component='details'
              sx={{
                p: 2.1,
                borderRadius: '8px',
                backgroundColor: '#f8fafc',
                border: '1px solid rgba(17,24,39,0.08)',
                '& summary': {
                  cursor: 'pointer',
                  fontWeight: 900,
                  color: '#111827',
                },
              }}
            >
              <Box component='summary'>{item.question}</Box>
              <Typography sx={{ mt: 1.2, color: '#4b5563', lineHeight: 1.75 }}>
                {item.answer}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function CtaSection() {
  return (
    <Box
      component='section'
      aria-labelledby='cta-atto1-title'
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 9 },
        backgroundColor: '#111827',
      }}
    >
      <Box
        sx={{ maxWidth: 980, mx: 'auto', textAlign: 'center', color: '#fff' }}
      >
        <Typography
          id='cta-atto1-title'
          component='h2'
          sx={{
            fontSize: { xs: '2rem', md: '3rem' },
            fontWeight: 900,
          }}
        >
          Tertarik dengan BYD Atto 1?
        </Typography>
        <Typography
          sx={{
            mt: 2,
            mx: 'auto',
            maxWidth: 780,
            color: 'rgba(255,255,255,0.8)',
            lineHeight: 1.75,
          }}
        >
          Hubungi Junius di BYD BSD Serpong untuk info harga BYD Atto 1, promo,
          simulasi pembelian, ketersediaan warna, dan jadwal test drive untuk
          area BSD, Tangerang, dan Gading Serpong.
        </Typography>
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Button
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20BYD%20Atto%201%20dan%20ingin%20tahu%20harga%20serta%20promonya'
            target='_blank'
            rel='noopener noreferrer'
            variant='contained'
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: '#1f5fd1',
              '&:hover': { backgroundColor: '#184ca8' },
            }}
          >
            Tanya Harga Atto 1
          </Button>
          <Button
            component={RouterLink}
            to='/blog/harga-byd-atto-1-bsd-serpong-junius'
            variant='outlined'
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 800,
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': {
                borderColor: '#fff',
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            Buka Halaman Harga Lengkap
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default JelajahiSeagull;
